import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import media from '@activebrands/core-web/config/media';
import { useLocalStorage } from '@activebrands/core-web/libs/local-storage';
import { transformButton } from '@activebrands/core-web/libs/storyblok/utils/transform-buttons';
import { transformInfoBoxes } from '@activebrands/core-web/libs/storyblok/utils/transform-info-boxes';
import { styled } from '@activebrands/core-web/libs/styletron';
import getBasketType from '@activebrands/core-web/utils/get-basket-type';
import StepList from 'components/StepList';
import Heading from 'components/text/Heading';
import ActRewardsSection from './ActRewardsSection';
import DeliverySection from './DeliverySection';
import PaymentSection from './PaymentSection';
import ShippingSection from './ShippingSection';
import SummarySection from './SummarySection';

const BASKET_LOCAL_STORAGE = process.env.REACT_APP_BASKET_LOCAL_STORAGE;
const ACT_REWARDS_ENABLED = process.env.REACT_APP_ENABLE_ACT_REWARDS === 'true';

const Wrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    justifyContent: 'space-between',
    maxWidth: 'var(--checkout-corridor-max-width)',
    marginLeft: 'auto',
    marginRight: 'auto',
    alignItems: 'flex-start',
    paddingBottom: 'var(--height-header)',

    [media.min['desktop.sm']]: {
        flexDirection: 'row',
        padding: '0',
    },
});

const MainColumn = styled('div', {
    width: '100%',
    paddingTop: '24px',
    paddingBottom: '64px',

    [media.min['desktop.sm']]: {
        width: '50%',
    },
});

const SidebarColumn = styled('div', {
    display: 'none',

    [media.min['desktop.sm']]: {
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        right: 0,
        top: 'var(--height-header)',
        width: '41.66666667%',
        height: 'calc(var(--vh) - var(--height-header))',
    },
});

const CheckoutView = ({ content }) => {
    const basket = useSelector(state => state.basket);
    const { address, totals, totalDiscount, items, currency } = basket;

    const basketType = getBasketType(items);

    const {
        actRewardsHeading,
        actRewardsParagraph,
        actRewardsUps,
        addressNewsletterText,
        addressSeprateShippingText,
        addressTermsText,
        bonusPointsInfo,
        bonusPointsPlaceholder,
        infoBoxes,
        loginButton,
        loginHeading,
        loginParagraph,
        orderLabel,
        previewStateOverride,
        signUpButton,
        summaryVoucherInput,
    } = content;

    const step = {
        shipping: 1,
        delivery: 2,
        payment: 3,
    };

    const transformedActRewardsHeading = actRewardsHeading?.[0]?.text;
    const transformedActRewardsParagraph = actRewardsParagraph?.[0]?.richText;
    const transformedActRewardsUsp = {
        label: actRewardsUps?.[0]?.label,
        icon: actRewardsUps?.[0]?.icon[0]?.icon,
    };

    const transformedLoginButton = loginButton?.length > 0 ? transformButton(loginButton[0]) : null;
    const transformedSignUpButton = signUpButton?.length > 0 ? transformButton(signUpButton[0]) : null;
    const transformedLoginParagraph = loginParagraph?.length > 0 ? loginParagraph?.[0]?.richText : null;
    const transformedLoginHeading = loginHeading?.length > 0 ? loginHeading?.[0]?.text : '';
    const transformedInfoBoxes = infoBoxes?.length > 0 ? transformInfoBoxes(infoBoxes) : [];
    const voucherCodePlaceholder = summaryVoucherInput?.[0]?.placeholder;
    const voucherLabel = summaryVoucherInput?.[0]?.label;

    const [currentCheckoutStep, setCurrentCheckoutStep] = useState(step.shipping);

    // Save data for checkout response page
    const [basketValue, setBasketValue] = useLocalStorage(BASKET_LOCAL_STORAGE);
    useEffect(() => {
        setBasketValue({ ...basketValue, address, totals, totalDiscount, items, currency });
    }, [address, totals, totalDiscount, items, currency]);

    const checkoutSteps = [
        {
            active: currentCheckoutStep === step.shipping,
            label: fm('Delivery'),
            onClick: () => setCurrentCheckoutStep(step.shipping),
            $style: { cursor: currentCheckoutStep !== step.shipping ? 'pointer' : 'default' },
        },
        {
            active: currentCheckoutStep === step.delivery,
            label: fm('Shipping'),
            onClick: () => currentCheckoutStep > step.delivery && setCurrentCheckoutStep(step.delivery),
            $style: { cursor: currentCheckoutStep > step.delivery ? 'pointer' : 'default' },
        },
        {
            active: currentCheckoutStep === step.payment,
            label: fm('Payment'),
        },
    ];

    return (
        <Wrapper>
            <MainColumn>
                <Heading
                    $style={{ padding: '0 var(--margin-content)' }}
                    as="h1"
                    fontKeys={['Primary/40_-05', null, null, null, null, 'Primary/48_-09']}
                >
                    {fm('Checkout')}
                </Heading>
                <StepList currentStep={currentCheckoutStep} items={checkoutSteps} />
                <ActRewardsSection
                    $style={{ marginTop: ['0', null, null, null, null, '24px'] }}
                    actRewardsParagraph={transformedActRewardsParagraph}
                    actRewardsHeading={transformedActRewardsHeading}
                    actRewardsusp={transformedActRewardsUsp}
                    isVouchersAvailable={ACT_REWARDS_ENABLED}
                    loginButton={transformedLoginButton}
                    loginHeading={transformedLoginHeading}
                    loginParagraph={transformedLoginParagraph}
                    checkoutCurrentStep={currentCheckoutStep}
                    checkoutStep={step.shipping}
                    signUpButton={transformedSignUpButton}
                />
                <SummarySection
                    $style={{ display: ['block', null, null, null, null, 'none'] }}
                    bonusPointsInfo={bonusPointsInfo}
                    bonusPointsPlaceholder={bonusPointsPlaceholder}
                    currentCheckoutStep={currentCheckoutStep}
                    finalPrice={totals?.finalPrice}
                    innerWrapperStyling={{ display: currentCheckoutStep > step.shipping ? 'none' : 'block' }}
                    orderLabel={orderLabel}
                    orderSummaryStep={step.shipping}
                    paymentStep={step.payment}
                    previewStateOverride={previewStateOverride}
                    voucherCodePlaceholder={voucherCodePlaceholder}
                    voucherLabel={voucherLabel}
                    setCurrentCheckoutStep={setCurrentCheckoutStep}
                />
                <ShippingSection
                    addressNewsletterText={addressNewsletterText?.[0].richText}
                    addressSeprateShippingText={addressSeprateShippingText?.[0].richText}
                    addressTermsText={addressTermsText?.[0].richText}
                    basketType={basketType}
                    currentCheckoutStep={currentCheckoutStep}
                    infoBoxes={transformedInfoBoxes}
                    setCurrentCheckoutStep={setCurrentCheckoutStep}
                    shippingStep={step.shipping}
                />
                <DeliverySection
                    $style={{ display: currentCheckoutStep >= step.delivery ? 'block' : 'none' }}
                    basketType={basketType}
                    currentCheckoutStep={currentCheckoutStep}
                    deliveryStep={step.delivery}
                    setCurrentCheckoutStep={setCurrentCheckoutStep}
                />
                <PaymentSection
                    $style={{ display: currentCheckoutStep >= step.payment ? 'block' : 'none' }}
                    bonusPointsInfo={bonusPointsInfo}
                    bonusPointsPlaceholder={bonusPointsPlaceholder}
                    currentCheckoutStep={currentCheckoutStep}
                    deliveryStep={step.delivery}
                    orderLabel={orderLabel}
                    paymentStep={step.payment}
                    setCurrentCheckoutStep={setCurrentCheckoutStep}
                    voucherCodePlaceholder={voucherCodePlaceholder}
                    voucherLabel={voucherLabel}
                />
            </MainColumn>
            <SidebarColumn>
                <SummarySection
                    bonusPointsInfo={bonusPointsInfo}
                    bonusPointsPlaceholder={bonusPointsPlaceholder}
                    currentCheckoutStep={currentCheckoutStep}
                    finalPrice={totals.finalPrice}
                    isVouchersAvailable={ACT_REWARDS_ENABLED}
                    orderLabel={orderLabel}
                    paymentStep={step.payment}
                    previewStateOverride={previewStateOverride}
                    setCurrentCheckoutStep={setCurrentCheckoutStep}
                    shippingStep={step.shipping}
                    voucherCodePlaceholder={voucherCodePlaceholder}
                    voucherLabel={voucherLabel}
                />
            </SidebarColumn>
        </Wrapper>
    );
};

CheckoutView.propTypes = {
    content: PropTypes.object.isRequired,
};

export default CheckoutView;
